import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY } from '../dews-iii-medical-history/dews-iii-medical-history.component';
import { DEWS_MEDICAL_HISTORY_FIELDS } from '../dews-iii-medical-history/dews-iii-medical-history.schema';
import { getIdentifiedDrivers } from '../dews-iii-drivers/dews-iii-drivers.component';
import { DewsIIIService, HOMEOSTASIS_CATEGORY_LABELS, HomeostasisResult, SymptomClassification } from '../dews-iii.service';

interface HomeostasisResultDisplay {
  code: string;
  label: string;
  resultLabel: string;
  resultClass: string;
}

interface FlagItem {
  label: string;
  active: boolean;
}

@Component({
  selector: 'csi-dews-iii-analytics-panel',
  templateUrl: 'dews-iii-analytics-panel.component.html',
  styleUrls: ['dews-iii-analytics-panel.component.scss']
})
export class DewsIIIAnalyticsPanelComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeFormGroup;

  collapsed = false;

  @HostBinding('class.collapsed') get isCollapsed() { return this.collapsed; }

  toggle() { this.collapsed = !this.collapsed; }

  osdiScore: number | null = null;
  symptomClassificationLabel = '—';
  symptomClassificationClass = '';
  diagnosisLabel = 'Awaiting Data';
  diagnosisBannerClass = 'banner-unknown';
  clinicianOverride = false;
  homeostasisResultDisplays: HomeostasisResultDisplay[] = [];
  allFlags: FlagItem[] = [];
  activeFlags: FlagItem[] = [];
  identifiedDrivers: string[] = [];

  constructor(private dewsIIIService: DewsIIIService) {}

  ngOnInit() {
    if (!this.formGroup) { return; }
    this.update();
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.update());
  }

  ngOnDestroy() {}

  private update() {
    this.osdiScore = this.computeOsdiScore();
    this.symptomClassificationLabel = this.computeSymptomClassificationLabel();
    this.symptomClassificationClass = this.computeSymptomClassificationClass();
    const diagnosisResult = this.dewsIIIService.determineDiagnosis(
      this.osdiScore,
      this.computeHomeostasisResultsRaw()
    );
    this.clinicianOverride = this.computeClinicianOverride();
    this.diagnosisLabel = this.dewsIIIService.diagnosisLabel(diagnosisResult, this.osdiScore);
    this.diagnosisBannerClass = this.clinicianOverride
      ? 'banner-diagnosed-override'
      : this.dewsIIIService.diagnosisBannerClass(diagnosisResult);
    this.homeostasisResultDisplays = this.computeHomeostasisResultDisplays();
    this.allFlags = this.computeAllFlags();
    this.activeFlags = this.allFlags.filter(function(f) { return f.active; });
    this.identifiedDrivers = this.computeIdentifiedDrivers();
  }

  private computeOsdiScore(): number | null {
    const control = this.formGroup.controls['osdi6'];
    if (control && control.value !== null && control.value !== undefined) {
      return control.value;
    }
    return null;
  }

  private computeSymptomClassificationClass(): string {
    if (this.osdiScore === null || this.osdiScore === undefined) {
      return '';
    }
    const classification: SymptomClassification = this.dewsIIIService.classifyOsdiScore(this.osdiScore);
    if (classification === 'normal') { return 'severity-normal'; }
    if (classification === 'mildToModerate') { return 'severity-mild'; }
    if (classification === 'severe') { return 'severity-severe'; }
    return '';
  }

  private computeSymptomClassificationLabel(): string {
    if (this.osdiScore === null || this.osdiScore === undefined) {
      return '—';
    }
    const classification: SymptomClassification = this.dewsIIIService.classifyOsdiScore(this.osdiScore);
    switch (classification) {
      case 'normal': return 'Normal';
      case 'mildToModerate': return 'Mild to Moderate';
      case 'severe': return 'Severe';
      default: return '—';
    }
  }

  private computeHomeostasisResultsRaw(): HomeostasisResult[] {
    const control = this.formGroup.controls['dewsHomeostasisResults'];
    if (control && control.value && Array.isArray(control.value)) {
      return control.value;
    }
    return [
      { category: 'tearFilmStability', performed: false, positive: false },
      { category: 'osmolarity', performed: false, positive: false },
      { category: 'ocularSurfaceStaining', performed: false, positive: false }
    ];
  }

  private computeHomeostasisResultDisplays(): HomeostasisResultDisplay[] {
    const control = this.formGroup.controls['dewsHomeostasisResults'];
    const results: HomeostasisResult[] =
      control && control.value && Array.isArray(control.value) ? control.value : [];

    return results.map(function(result) {
      const meta = HOMEOSTASIS_CATEGORY_LABELS[result.category];
      const resultClass = result.performed
        ? (result.positive ? 'result-positive' : 'result-negative')
        : 'result-not-done';
      return {
        code: meta ? meta.code : result.category,
        label: meta ? meta.label : result.category,
        resultLabel: result.performed ? (result.positive ? 'Positive' : 'Negative') : 'Not Done',
        resultClass: resultClass
      };
    });
  }

  private computeAllFlags(): FlagItem[] {
    const nestedGroup = this.formGroup.controls[DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY] as FormGroup;
    const controls = nestedGroup ? nestedGroup.controls : {};
    return DEWS_MEDICAL_HISTORY_FIELDS.map(f => ({
      label: f.flagLabel,
      active: !!(controls[f.key] && controls[f.key].value === f.expected)
    }));
  }

  private computeIdentifiedDrivers(): string[] {
    return getIdentifiedDrivers(this.formGroup.controls);
  }

  private computeClinicianOverride(): boolean {
    const control = this.formGroup.controls['dewsClinicianOverride'];
    return control && control.value === true;
  }

}
